import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getDashboardStats,
  getUserProductivity,
  getProjectReports,
  getGanttData,
  DashboardStats,
  UserProductivity,
  ProjectReport,
  GanttTask,
} from '../../services/reportService';

interface ReportState {
  dashboardStats: DashboardStats | null;
  userProductivity: UserProductivity[];
  projectReports: ProjectReport[];
  ganttData: GanttTask[];
  loading: boolean;
  error: string | null;
}

const initialState: ReportState = {
  dashboardStats: null,
  userProductivity: [],
  projectReports: [],
  ganttData: [],
  loading: false,
  error: null,
};

export const fetchDashboardStats = createAsyncThunk(
  'report/fetchDashboardStats',
  async () => {
    return await getDashboardStats();
  }
);

export const fetchUserProductivity = createAsyncThunk(
  'report/fetchUserProductivity',
  async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
    return await getUserProductivity(startDate, endDate);
  }
);

export const fetchProjectReports = createAsyncThunk(
  'report/fetchProjectReports',
  async () => {
    return await getProjectReports();
  }
);

export const fetchGanttData = createAsyncThunk(
  'report/fetchGanttData',
  async (projectId: string) => {
    return await getGanttData(projectId);
  }
);

const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Dashboard Stats
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action: PayloadAction<DashboardStats>) => {
        state.loading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard stats';
      });

    // User Productivity
    builder
      .addCase(fetchUserProductivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProductivity.fulfilled, (state, action: PayloadAction<UserProductivity[]>) => {
        state.loading = false;
        state.userProductivity = action.payload;
      })
      .addCase(fetchUserProductivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch user productivity';
      });

    // Project Reports
    builder
      .addCase(fetchProjectReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectReports.fulfilled, (state, action: PayloadAction<ProjectReport[]>) => {
        state.loading = false;
        state.projectReports = action.payload;
      })
      .addCase(fetchProjectReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch project reports';
      });

    // Gantt Data
    builder
      .addCase(fetchGanttData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGanttData.fulfilled, (state, action: PayloadAction<GanttTask[]>) => {
        state.loading = false;
        state.ganttData = action.payload;
      })
      .addCase(fetchGanttData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch Gantt data';
      });
  },
});

export const { clearError } = reportSlice.actions;
export default reportSlice.reducer;
